// src/pages/AddListing/AddListing.jsx
import React, { useState, useEffect } from "react";
import { db, auth } from "../../services/firebase";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp, updateDoc, doc, getDoc } from "firebase/firestore";
import SellerOnboarding from "../../components/SellerOnboarding";
import { LOADING_STYLES } from "../../constants";
import ListingForm from './ListingForm';
import { uploadListingImages } from './uploadUtils';
import { formatFirestoreError, formatStorageError } from "../../utils/errorMessages";

function AddListing() {
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    category: "",
    quantity: "1",
  });

  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sellerStatus, setSellerStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const navigate = useNavigate();
  const user = auth.currentUser;

  // Check seller status on component mount
  useEffect(() => {
    const checkSellerStatus = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const sellerDoc = await getDoc(doc(db, 'sellerProfiles', user.uid));
        if (sellerDoc.exists()) {
          const data = sellerDoc.data();
          setSellerStatus(data.stripeAccountStatus || 'pending');
        } else {
          setSellerStatus('not_created');
        }
      } catch (error) {
        setSellerStatus('error');
      } finally {
        setCheckingStatus(false);
      }
    };

    checkSellerStatus();
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSellerOnboardingComplete = () => {
    setSellerStatus('active');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Ensure user is logged in
    if (!user) {
      setError("You must be logged in to add a listing.");
      return;
    }

    // Validate form data
    const { title, description, price, category, quantity } = formData;
    if (!title || !description || !price || !category || !quantity) {
      setError("Please fill out all fields.");
      return;
    }

    // Validate price
    const priceNum = Number(price);
    if (Number.isNaN(priceNum) || priceNum < 0) {
      setError("Please enter a valid, non-negative price.");
      return;
    }

    // Validate quantity
    const quantityNum = Number(quantity);
    if (Number.isNaN(quantityNum) || quantityNum < 1 || !Number.isInteger(quantityNum)) {
      setError("Please enter a valid quantity (must be at least 1).");
      return;
    }

    // Validate images
    if (files.length === 0) {
      setError("Please upload at least one image.");
      return;
    }

    setLoading(true);

    try {
      // Create listing document without images first
      const listingData = {
        title: title.trim(),
        description: description.trim(),
        price: priceNum,
        category: category.trim(),
        quantity: quantityNum,
        quantityAvailable: quantityNum, // Track remaining quantity
        sellerId: user.uid,
        createdAt: serverTimestamp(),
        status: "active",
        images: [], // Will be updated after upload
      };

      const docRef = await addDoc(collection(db, "listings"), listingData);

      // Upload images and get URLs
      const imageUrls = await uploadListingImages(docRef.id, files);

      // Update the listing with image URLs
      await updateDoc(docRef, {
        images: imageUrls,
      });

      // Reset form
      setFormData({ title: "", description: "", price: "", category: "", quantity: "1" });
      setFiles([]);
      setPreviews([]);

      // Navigate to the new listing
      navigate(`/listing/${docRef.id}`);
    } catch (err) {
      // Format error message based on error type
      let errorMessage;
      if (err.code?.startsWith('storage/')) {
        errorMessage = formatStorageError(err);
      } else {
        errorMessage = formatFirestoreError(err);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div style={LOADING_STYLES.container}>
        <div style={LOADING_STYLES.spinner}></div>
        <p>Checking seller status...</p>
      </div>
    );
  }

  if (sellerStatus !== 'active') {
    return (
      <div style={{ margin: '50px auto', maxWidth: '800px', padding: '20px' }}>
        <SellerOnboarding user={user} onComplete={handleSellerOnboardingComplete} />
      </div>
    );
  }

  return (
    <ListingForm 
      formData={formData}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      files={files}
      setFiles={setFiles}
      previews={previews}
      setPreviews={setPreviews}
      error={error}
      setError={setError}
      loading={loading}
    />
  );
}

export default AddListing;